import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, FlatList, Text } from "native-base";
import { useCallback, useMemo } from "react";
import { formatDateTime } from "@madmedical/utils";
import { useTranslation } from "@madmedical/i18n";
import { useRouteNavigate } from "../providers/routing";
import ReactTable from "../molecules/ReactTable";
import ActionButton from "../molecules/ActionButton";
import { useDocuments } from "../providers/document";
import useResponsive from "../util/useResponsive";
import DocumentListItem from "../molecules/ListItem/DocumentListItem";
import useDownloadPreview from "../util/useDownloadPreview";
import { cropFilename } from "../util/strings";
import Tag from "../atoms/Tag";
import Spinner from "../atoms/Spinner";
import { DocumentService } from "../form/upload/model";
const DocumentsTableGeneral = ({ showOnlyGeneral, onDeletePress, onEndReached, withoutAction = false, }) => {
    const { documents, onSortChange } = useDocuments(showOnlyGeneral ? "general" : "both");
    const navigate = useRouteNavigate();
    const { download, isDownloading } = useDownloadPreview();
    const { isSmallScreen } = useResponsive();
    const { t } = useTranslation();
    const handleRowPress = useCallback(({ id }) => {
        navigate("document", { documentId: id });
    }, [navigate]);
    const handleViewPress = useCallback((documentId) => () => {
        navigate("document", { documentId });
    }, [navigate]);
    const getTagVariant = useCallback((service) => {
        switch (service) {
            case DocumentService.Pregnancy:
                return "violet";
            case DocumentService.PrePostOp:
                return "blue";
            default:
                return "lime";
        }
    }, []);
    const columns = useMemo(() => [
        {
            Header: t("documentName"),
            accessor: "filename",
            width: "auto",
            defaultCanSort: !!onSortChange,
            Cell: ({ value }) => (_jsx(Text, Object.assign({ numberOfLines: 1 }, { children: cropFilename(value, isSmallScreen ? 20 : 40) }))),
        },
        {
            Header: t("package"),
            accessor: "service",
            width: "auto",
            defaultCanSort: !!onSortChange,
            Cell: ({ value }) => value ? (_jsx(Tag, Object.assign({ variant: getTagVariant(value.value), size: "sm" }, { children: value === null || value === void 0 ? void 0 : value.forHumans }))) : (_jsx(Text, { children: "-" })),
        },
        {
            Header: t("uploadDate"),
            accessor: "createdAt",
            defaultCanSort: !!onSortChange,
            width: 200,
            Cell: ({ value: createdAt }) => (_jsx(_Fragment, { children: formatDateTime(createdAt) })),
        },
        {
            Header: "",
            id: "action",
            width: 40,
            Cell: ({ row: { original: document }, }) => (_jsx(Box, Object.assign({ flexDirection: "row", justifyContent: "flex-end", w: "full" }, { children: _jsx(ActionButton, { actions: [
                        {
                            text: t("view"),
                            iconName: "documentView",
                            onPress: handleViewPress(document.id),
                        },
                        {
                            text: t("download"),
                            iconName: "documentDownload",
                            onPress: () => {
                                void download(document);
                            },
                        },
                        ...(onDeletePress
                            ? [
                                {
                                    text: t("delete"),
                                    iconName: "trashCan",
                                    onPress: () => {
                                        onDeletePress(document.id);
                                    },
                                },
                            ]
                            : []),
                    ] }) }))),
        },
    ], [
        onSortChange,
        isSmallScreen,
        handleViewPress,
        download,
        onDeletePress,
        getTagVariant,
        t,
    ]);
    if (withoutAction) {
        columns.pop();
    }
    if (isDownloading) {
        _jsx(Spinner, {});
    }
    return isSmallScreen ? (_jsx(FlatList, { minWidth: "full", showsVerticalScrollIndicator: false, data: documents, renderItem: ({ item }) => (_jsx(DocumentListItem, { document: item, onDeletePress: onDeletePress })), keyExtractor: (item) => item.id, overflow: "hidden", onEndReached: onEndReached })) : (_jsx(ReactTable, { columns: columns, data: documents, onSortChange: onSortChange, onEndReached: onEndReached, onPress: handleRowPress }));
};
export default DocumentsTableGeneral;
